import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BRAND_COLORS } from '../../../styles/theme';
import { DashboardData, DashboardFilters } from '../types/dashboard.types';
import { MaintenanceScheduleCard, InspectionInfoDialog } from './components';
import { ViewMaintenanceRecordDialog } from './maintenance/components/ViewMaintenanceRecordDialog';
import { MaintenanceRecord } from '../../../types/api';
import { useOpenMaintenanceManualMutation } from './maintenance.mutations';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useTranslation } from '../../../i18n';
import { useAuthV2 } from '../../../contexts/AuthContextV2';
import { canManageMaintenance as checkCanManageMaintenance } from '../../../config/roles';

// Maintenance interfaces and types
interface MaintenanceItem {
  key: string;
  display_name: string;
  interval_hours: number;
  time_remaining: number;
  overdue: boolean;
  alerts_suppressed?: boolean;
  hours_since_maintenance?: number;
}

// Visual inspection instructions data
const VISUAL_INSPECTION_INSTRUCTIONS: Record<string, string> = {
  vi_blades_motors: 'Examine for any wear, damage, or loose components',
  vi_star_assembly:
    'Examine for damage or excessive play in joints\nExamine glued connections (composite and aluminium parts)',
  vi_main_frame:
    'Examine for damage or loose components\n\n⚠️ WARNING: pay particular attention to any damage on the main frame, interface panel, hybrid system cover and payload connection.\n\nExamine glued connections (composite and aluminium parts)\n\n⚠️ WARNING: pay particular attention to fuel tank to leg connection and leg attachment to the main frame.',
  vi_battery: 'Check battery charge state and balance, charge, or balance if necessary',
  vi_fuel_tanks:
    'Examine for damage or any leakage (static and pressured by powering up the UAS).\n\nInspection of fuel system including fuel flow lines, fuel pumps, tanks, hoses, clamps, connectors, and others for leakages, damages wear etc',
  vi_hybrid_system:
    'Examine for damage and loose components\n\nCables for loose connections, damaged insulations, and other\n\nExamine muffler for damage or loose connection\n\nExamine generator for damage',
  vi_data_handling:
    'Upload operational UAS data (Mission Planner and XFD logs) and inspection results to Xer',
  vi_systems:
    'Removal of top cover\n\nExamine for damage and loose components\n\nCables for loose connections, damaged insulations, and other\n\nInstall top cover',
};

// Maintenance manual data - maps to PDF files in S3 (maintenance-docs/manual/)
const MAINTENANCE_MANUALS: Record<string, string> = {
  // System maintenance
  '10um_filter': 'fuel-filter-replacement.pdf',
  '60um_filter': 'fuel-filter-replacement.pdf',
  air_filter: 'air-filter-replacement.pdf',
  // Note: injector manual not available yet

  // Component maintenance
  spark_plugs: 'spark-plugs-replacement.pdf',
  hybrid_pack: 'hybrid-pack-replacement.pdf',
  batteries: 'batteries-replacement.pdf',
  propellers: 'propellers-replacement.pdf',
  fuel_pumps: 'fuel-pumps-replacement.pdf',
};

type MaintenanceScheduleEntry = {
  display_name: string;
  interval_hours: number;
  time_remaining: number;
  overdue: boolean;
  alerts_suppressed?: boolean;
  hours_since_maintenance?: number;
};

// Helper function to get maintenance items by category from API
const getMaintenanceItemsByCategory = (droneStats: {
  maintenance_schedule?: Record<string, MaintenanceScheduleEntry>;
}): {
  component: MaintenanceItem[];
  visual_inspection: MaintenanceItem[];
} => {
  const component: MaintenanceItem[] = [];
  const visual_inspection: MaintenanceItem[] = [];

  // All maintenance items now come from the API (maintenance_schedule)
  if (droneStats.maintenance_schedule) {
    Object.entries(droneStats.maintenance_schedule).forEach(([key, item]) => {
      const maintenanceItem = item as MaintenanceScheduleEntry;

      const itemData = {
        key,
        display_name: maintenanceItem.display_name,
        interval_hours: maintenanceItem.interval_hours,
        time_remaining: maintenanceItem.time_remaining,
        overdue: maintenanceItem.overdue,
        alerts_suppressed: maintenanceItem.alerts_suppressed,
        hours_since_maintenance: maintenanceItem.hours_since_maintenance,
      };

      // Categorize based on key prefix
      if (key.startsWith('vi_')) {
        visual_inspection.push(itemData);
      } else {
        // Component maintenance (includes both system and component maintenance)
        component.push(itemData);
      }
    });
  }

  return { component, visual_inspection };
};

// Helper function to check if any component is overdue
const getComponentOverdueStatus = (droneStats: {
  maintenance_schedule?: Record<string, MaintenanceScheduleEntry>;
}): boolean => {
  const categories = getMaintenanceItemsByCategory(droneStats);
  return categories.component.some((item) => item.overdue);
};

// Helper function to check if any visual inspection is overdue
const getVisualInspectionOverdueStatus = (droneStats: {
  maintenance_schedule?: Record<string, MaintenanceScheduleEntry>;
}): boolean => {
  const categories = getMaintenanceItemsByCategory(droneStats);
  return categories.visual_inspection.some((item) => item.overdue);
};

// Timeline components and hooks - all extracted from this file for better maintainability
import {
  UpcomingMaintenanceListView,
  HistoricalMaintenanceListView,
  CreateMaintenanceRecordDialog,
  useMaintenanceManagement,
} from './maintenance';
import { useUpcomingMaintenance, useMaintenanceHistory } from './maintenance/hooks';

// Styled components for records panel
const NotesPanel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const NotesContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '12px',
  maxHeight: '500px', // Limit height to make scrollable
  minHeight: '400px',
});

// Styled components for maintenance history panel
const MaintenanceHistoryContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const MaintenanceHistoryHeader = styled(Box)({
  padding: '16px',
  borderBottom: '1px solid #e0e0e0',
});

const MaintenanceHistoryContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '12px',
  maxHeight: '500px',
  minHeight: '400px',
});

interface MaintenanceTabProps {
  data: DashboardData;
  filters: DashboardFilters;
  onFilterChange: (filters: Partial<DashboardFilters>) => void;
}

export const MaintenanceTab: React.FC<MaintenanceTabProps> = ({
  data,
  filters,
  onFilterChange: _onFilterChange,
}) => {
  void _onFilterChange; // Required by common tab interface but not used in this tab

  const { t } = useTranslation();
  const { showSuccess } = useErrorHandler();
  const openManualMutation = useOpenMaintenanceManualMutation();
  const { profile } = useAuthV2();
  const activityListRef = useRef<HTMLDivElement>(null);

  // Check if user can manage maintenance records (admin or maintenance role)
  const canManageMaintenance = checkCanManageMaintenance(profile?.organization?.roles);

  // Maintenance schedule state
  const [componentScheduleExpanded, setComponentScheduleExpanded] = useState(false);
  const [visualInspectionScheduleExpanded, setVisualInspectionScheduleExpanded] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedInspectionInfo, setSelectedInspectionInfo] = useState<{
    title: string;
    instructions: string;
  } | null>(null);

  // Custom hooks for maintenance management
  const maintenanceManagement = useMaintenanceManagement(filters.selectedSerial);
  const upcomingMaintenanceData = useUpcomingMaintenance(filters.selectedSerial);
  const maintenanceHistoryData = useMaintenanceHistory(filters.selectedSerial);

  // Dialog state for activity details
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState<MaintenanceRecord | null>(null);

  // Extract droneStats for maintenance schedules
  const { droneStats } = data;

  // Handler for opening inspection info dialog
  const handleInspectionInfoClick = (item: MaintenanceItem) => {
    const instructions = VISUAL_INSPECTION_INSTRUCTIONS[item.key];
    if (instructions) {
      setSelectedInspectionInfo({
        title: item.display_name,
        instructions,
      });
      setInfoDialogOpen(true);
    }
  };

  // Handler for maintenance manual - fetches presigned URL from S3 and opens in new tab
  const handleMaintenanceManualDownload = async (item: MaintenanceItem) => {
    const manualFile = MAINTENANCE_MANUALS[item.key];
    if (manualFile) {
      try {
        // Fetch presigned URL from backend (handles auth automatically)
        const result = await openManualMutation.mutateAsync(manualFile);
        if (result.url) {
          window.open(result.url, '_blank', 'noopener,noreferrer');
          showSuccess('Manual opened successfully');
        }
      } catch {
        // Error toast handled by global MutationCache handler via meta.errorMessage
      }
    }
  };

  // Handle upcoming maintenance click to open dialog
  const handleUpcomingMaintenanceClick = useCallback((maintenance: MaintenanceRecord) => {
    console.log('Upcoming maintenance clicked:', maintenance);
    setSelectedMaintenance(maintenance);
    setDialogOpen(true);
  }, []);

  // Handle dialog close
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedMaintenance(null);
  }, []);

  if (!filters.selectedSerial) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          {t('maintenanceTab.selectDrone')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Main content container */}
      <Box
        sx={{
          display: 'flex',
          gap: '24px',
          '@media (max-width: 900px)': {
            flexDirection: 'column',
          },
        }}
      >
        {/* Upcoming Maintenance Panel - Left side, aligned with drone dropdown */}
        <Box
          ref={activityListRef}
          sx={{
            flex: 1,
            minWidth: '200px',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            '@media (max-width: 900px)': {
              minWidth: '100%',
            },
          }}
        >
          <NotesPanel>
            <Box
              sx={{
                padding: '16px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: BRAND_COLORS.forestGreen.main }}
              >
                {t('maintenanceTab.upcomingMaintenance')}
              </Typography>
              {canManageMaintenance && (
                <Button
                  data-testid="maintenance-add-button"
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={maintenanceManagement.handleOpenCreateDialog}
                >
                  {t('maintenanceTab.addMaintenance')}
                </Button>
              )}
            </Box>
            <NotesContent>
              <UpcomingMaintenanceListView
                maintenanceRecords={upcomingMaintenanceData.upcomingMaintenance}
                loading={upcomingMaintenanceData.loading}
                error={upcomingMaintenanceData.error}
                onMaintenanceClick={handleUpcomingMaintenanceClick}
              />
            </NotesContent>
          </NotesPanel>
        </Box>

        {/* Maintenance History Panel - Right side */}
        <Box
          sx={{
            flex: 1,
            minWidth: '200px',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            '@media (max-width: 900px)': {
              minWidth: '100%',
            },
          }}
        >
          <MaintenanceHistoryContainer>
            <MaintenanceHistoryHeader>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: BRAND_COLORS.forestGreen.main }}
              >
                {t('maintenanceTab.maintenanceHistory')}
              </Typography>
            </MaintenanceHistoryHeader>
            <MaintenanceHistoryContent>
              <HistoricalMaintenanceListView
                maintenanceRecords={maintenanceHistoryData.maintenanceHistory}
                loading={maintenanceHistoryData.loading}
                error={maintenanceHistoryData.error}
                droneSerial={filters.selectedSerial}
                onRefresh={maintenanceHistoryData.refresh}
              />
            </MaintenanceHistoryContent>
          </MaintenanceHistoryContainer>
        </Box>
      </Box>

      {/* Component Maintenance Schedule */}
      {filters.selectedSerial && droneStats && (
        <Box sx={{ mt: 2 }}>
          <MaintenanceScheduleCard
            title={t('maintenanceTab.componentMaintenanceSchedule')}
            items={getMaintenanceItemsByCategory(droneStats).component}
            expanded={componentScheduleExpanded}
            onToggleExpanded={() => setComponentScheduleExpanded(!componentScheduleExpanded)}
            hasOverdueItems={getComponentOverdueStatus(droneStats)}
            showManualButton={true}
            onManualDownload={handleMaintenanceManualDownload}
            manualAvailable={(itemKey) => !!MAINTENANCE_MANUALS[itemKey]}
          />
        </Box>
      )}

      {/* Visual Inspection Maintenance Schedule */}
      {filters.selectedSerial && droneStats && (
        <Box sx={{ mt: 2 }}>
          <MaintenanceScheduleCard
            title={t('maintenanceTab.visualInspectionSchedule')}
            items={getMaintenanceItemsByCategory(droneStats).visual_inspection}
            expanded={visualInspectionScheduleExpanded}
            onToggleExpanded={() =>
              setVisualInspectionScheduleExpanded(!visualInspectionScheduleExpanded)
            }
            hasOverdueItems={getVisualInspectionOverdueStatus(droneStats)}
            onItemInfoClick={handleInspectionInfoClick}
          />
        </Box>
      )}

      {/* Inspection Info Dialog */}
      <InspectionInfoDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        inspectionInfo={selectedInspectionInfo}
      />

      {/* Note resolution dialog removed - notes handled separately */}

      {/* Maintenance Dialog */}
      <CreateMaintenanceRecordDialog
        open={maintenanceManagement.createDialogOpen}
        onClose={maintenanceManagement.handleCloseCreateDialog}
        onSubmit={maintenanceManagement.handleCreateMaintenance}
        droneSerial={filters.selectedSerial || ''}
        loading={maintenanceManagement.loading}
        error={maintenanceManagement.error}
        selectedFiles={maintenanceManagement.selectedFiles}
        onFileSelect={maintenanceManagement.handleFileSelect}
        onRemoveFile={maintenanceManagement.handleRemoveFile}
        uploadingFiles={maintenanceManagement.uploadingFiles}
      />

      {/* Event Details Dialog - for viewing activity details */}
      <ViewMaintenanceRecordDialog
        open={dialogOpen}
        selectedMaintenance={selectedMaintenance}
        onClose={handleCloseDialog}
        droneSerial={filters.selectedSerial || ''}
      />
    </>
  );
};
